import React, { createContext, useContext, useReducer, useRef } from 'react';
import type { CompanyInfo } from '@/services/accountService/companyService';
import type { CompanyAnnualSelectItem } from '@/services/accountService/companyAnnualService';
import type { ContentSheetData, MatchedCompany } from '@/services/auditService/auditFsAnnualService';
import type { FileUploadDialogActionRef } from '@/pages/components/common/FileUploadDialog';
import type { CompanyCrValidateHandle } from '@/pages/components/company-info/company/useCompanyCrGovLookup';

export type FsCreateState = {
  selectedCompany: CompanyInfo | null;
  selectedAnnual: CompanyAnnualSelectItem | null;
  annualId: number | null;
  isCreatingAnnual: boolean;
  isCreatingCompany: boolean;
  uploadPanelOpen: boolean;
  uploadCanSubmit: boolean;
  uploadUploading: boolean;
  bsUnbalanced: boolean;
  /** BS-EQ 与 PL-PAT 最终计算值均为 0 时为 true，隐藏 Submit */
  fsHideSubmit: boolean;
  getLines?: () => { bsLines: Array<{ fsId: number; amount: number }>; plLines: Array<{ fsId: number; amount: number }> } | null;
  beforeNext?: (clearDraft?: boolean) => Promise<boolean>;
  getFormData?: () => any;
  isSaving?: boolean;
  isProcessing?: boolean;
  // 数据暂存
  draftData?: {
    companyId: number;
    annualId: number | null;
    formData: any;
  } | null;
  // Content Sheet import data (one-time upload, shared across steps)
  contentData?: ContentSheetData | null;
  importedCompany?: MatchedCompany | null;
  companyMatched?: boolean;
  companyNameUpdated?: boolean;
  // Phase 3: Excel B6.1/B6.2 Redis cache token
  importToken?: string | null;
  /** Seata global transaction id for wizard flow */
  seataXid?: string | null;
  /** Current wizard step (0-3), synced for dialog close handling */
  activeStep: number;
  /** 通过 Create Company 流程新建的公司（UI Back 编辑态） */
  companyCreatedViaWizard: boolean;
  /**
   * Wizard 内新建的 companyId；null = Autocomplete/查询选中的已有公司。
   * Cancel/Seata rollback 仅当此字段有值时才补偿删除 company。
   */
  wizardCreatedCompanyId: number | null;
  /** 从 Step 1 返回 Step 0 后展示 CompanyDetail 编辑态 */
  companyEditOnStep0: boolean;
  /** importExcel 检测到 assessable year 已存在时的警告信息 */
  assessableYearConflict: string | null;
};

const initialState: FsCreateState = {
  selectedCompany: null,
  selectedAnnual: null,
  annualId: null,
  isCreatingAnnual: false,
  isCreatingCompany: false,
  uploadPanelOpen: false,
  uploadCanSubmit: false,
  uploadUploading: false,
  bsUnbalanced: false,
  fsHideSubmit: false,
  getLines: undefined,
  beforeNext: undefined,
  getFormData: undefined,
  isSaving: false,
  isProcessing: false,
  draftData: null,
  contentData: null,
  importedCompany: null,
  companyMatched: false,
  companyNameUpdated: false,
  importToken: null,
  seataXid: null,
  activeStep: 0,
  companyCreatedViaWizard: false,
  wizardCreatedCompanyId: null,
  companyEditOnStep0: false,
  assessableYearConflict: null,
};

export type FsCreateAction =
  | { type: 'set_company'; payload: CompanyInfo | null }
  | { type: 'set_annual'; payload: CompanyAnnualSelectItem | null }
  | { type: 'set_annual_id'; payload: number | null }
  | { type: 'set_is_creating_annual'; payload: boolean }
  | { type: 'set_is_creating_company'; payload: boolean }
  | { type: 'set_upload_panel_open'; payload: boolean }
  | { type: 'set_upload_panel_state'; payload: { canSubmit: boolean; uploading: boolean } }
  | { type: 'set_bs_unbalanced'; payload: boolean }
  | { type: 'set_fs_hide_submit'; payload: boolean }
  | { type: 'set_get_lines'; payload: FsCreateState['getLines'] }
  | { type: 'set_before_next'; payload: FsCreateState['beforeNext'] }
  | { type: 'set_get_form_data'; payload: FsCreateState['getFormData'] }
  | { type: 'set_is_saving'; payload: boolean }
  | { type: 'set_is_processing'; payload: boolean }
  | { type: 'save_draft'; payload: FsCreateState['draftData'] }
  | { type: 'clear_draft' }
  | { type: 'set_content_data'; payload: ContentSheetData | null }
  | { type: 'set_imported_company'; payload: MatchedCompany | null }
  | { type: 'set_import_flags'; payload: { companyMatched?: boolean; companyNameUpdated?: boolean } }
  | { type: 'set_import_token'; payload: string | null }
  | { type: 'set_seata_xid'; payload: string | null }
  | { type: 'set_active_step'; payload: number }
  | { type: 'set_company_created_via_wizard'; payload: boolean }
  /** null = queried/selected existing company; number = created in this wizard */
  | { type: 'set_wizard_created_company_id'; payload: number | null }
  | { type: 'set_company_edit_on_step0'; payload: boolean }
  | { type: 'set_assessable_year_conflict'; payload: string | null };

function reducer(state: FsCreateState, action: FsCreateAction): FsCreateState {
  switch (action.type) {
    case 'set_company':
      return { ...state, selectedCompany: action.payload, selectedAnnual: null, annualId: null, isCreatingAnnual: false, isCreatingCompany: false };
    case 'set_annual':
      return { ...state, selectedAnnual: action.payload, isCreatingAnnual: false };
    case 'set_annual_id':
      return { ...state, annualId: action.payload };
    case 'set_is_creating_annual':
      return { ...state, isCreatingAnnual: action.payload };
    case 'set_is_creating_company':
      return { ...state, isCreatingCompany: action.payload };
    case 'set_upload_panel_open':
      return { ...state, uploadPanelOpen: action.payload };
    case 'set_upload_panel_state':
      return {
        ...state,
        uploadCanSubmit: action.payload.canSubmit,
        uploadUploading: action.payload.uploading,
      };
    case 'set_bs_unbalanced':
      return state.bsUnbalanced === action.payload ? state : { ...state, bsUnbalanced: action.payload };
    case 'set_fs_hide_submit':
      return state.fsHideSubmit === action.payload ? state : { ...state, fsHideSubmit: action.payload };
    case 'set_get_lines':
      return { ...state, getLines: action.payload };
    case 'set_before_next':
      return { ...state, beforeNext: action.payload };
    case 'set_get_form_data':
      return { ...state, getFormData: action.payload };
    case 'set_is_saving':
      return { ...state, isSaving: action.payload };
    case 'set_is_processing':
      return { ...state, isProcessing: action.payload };
    case 'save_draft':
      return { ...state, draftData: action.payload };
    case 'clear_draft':
      return {
        ...state,
        draftData: null,
        companyCreatedViaWizard: false,
        wizardCreatedCompanyId: null,
        companyEditOnStep0: false,
      };
    case 'set_content_data':
      return { ...state, contentData: action.payload };
    case 'set_imported_company':
      return { ...state, importedCompany: action.payload };
    case 'set_import_flags':
      return { ...state, ...action.payload };
    case 'set_import_token':
      return { ...state, importToken: action.payload };
    case 'set_seata_xid':
      return { ...state, seataXid: action.payload };
    case 'set_active_step':
      return { ...state, activeStep: action.payload };
    case 'set_company_created_via_wizard':
      return {
        ...state,
        companyCreatedViaWizard: action.payload,
        wizardCreatedCompanyId: action.payload ? state.wizardCreatedCompanyId : null,
      };
    case 'set_wizard_created_company_id': {
      const id = action.payload != null && action.payload > 0 ? action.payload : null;
      return {
        ...state,
        wizardCreatedCompanyId: id,
        companyCreatedViaWizard: id != null,
      };
    }
    case 'set_company_edit_on_step0':
      return { ...state, companyEditOnStep0: action.payload };
    case 'set_assessable_year_conflict':
      return { ...state, assessableYearConflict: action.payload };
    default:
      return state;
  }
}

const Ctx = createContext<{
  state: FsCreateState;
  dispatch: React.Dispatch<FsCreateAction>;
  fileUploadActionRef: React.MutableRefObject<FileUploadDialogActionRef | null>;
  /** Step1 Excel Validate → CompanyDetail CR 校验 */
  crValidateRef: React.MutableRefObject<CompanyCrValidateHandle | null>;
} | null>(null);

export function FsCreateProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const fileUploadActionRef = useRef<FileUploadDialogActionRef | null>(null);
  const crValidateRef = useRef<CompanyCrValidateHandle | null>(null);
  return (
    <Ctx.Provider value={{ state, dispatch, fileUploadActionRef, crValidateRef }}>
      {children}
    </Ctx.Provider>
  );
}

export function useFsCreateCtx() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useFsCreateCtx must be used within FsCreateProvider');
  return ctx;
}
